import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Plus, Edit, Trash2, Eye, Phone, 
  ChevronLeft, ChevronRight, RefreshCw, AlertCircle, X,
  User, Calendar, MapPin, Mail, FileText, CreditCard
} from 'lucide-react';
import { customersAPI } from '../../services/api';

const CustomersList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [editCountryCode, setEditCountryCode] = useState('+966');

  const limit = 10;

  const countries = [
    { code: '+966', flag: '🇸🇦', name: 'السعودية' },
    { code: '+971', flag: '🇦🇪', name: 'الإمارات' },
    { code: '+965', flag: '🇰🇼', name: 'الكويت' },
    { code: '+973', flag: '🇧🇭', name: 'البحرين' },
    { code: '+974', flag: '🇶🇦', name: 'قطر' },
    { code: '+968', flag: '🇴🇲', name: 'عمان' },
    { code: '+967', flag: '🇾🇪', name: 'اليمن' },
    { code: '+964', flag: '🇮🇶', name: 'العراق' },
    { code: '+962', flag: '🇯🇴', name: 'الأردن' },
    { code: '+963', flag: '🇸🇾', name: 'سوريا' },
    { code: '+961', flag: '🇱🇧', name: 'لبنان' },
    { code: '+970', flag: '🇵🇸', name: 'فلسطين' },
    { code: '+20', flag: '🇪🇬', name: 'مصر' },
    { code: '+249', flag: '🇸🇩', name: 'السودان' },
    { code: '+218', flag: '🇱🇾', name: 'ليبيا' },
    { code: '+216', flag: '🇹🇳', name: 'تونس' },
    { code: '+213', flag: '🇩🇿', name: 'الجزائر' },
    { code: '+212', flag: '🇲🇦', name: 'المغرب' },
    { code: '+91', flag: '🇮🇳', name: 'الهند' },
    { code: '+92', flag: '🇵🇰', name: 'باكستان' },
    { code: '+880', flag: '🇧🇩', name: 'بنغلاديش' },
    { code: '+63', flag: '🇵🇭', name: 'الفلبين' },
    { code: '+62', flag: '🇮🇩', name: 'إندونيسيا' },
    { code: '+60', flag: '🇲🇾', name: 'ماليزيا' },
    { code: '+90', flag: '🇹🇷', name: 'تركيا' },
    { code: '+44', flag: '🇬🇧', name: 'بريطانيا' },
    { code: '+1', flag: '🇺🇸', name: 'أمريكا' },
    { code: '+33', flag: '🇫🇷', name: 'فرنسا' },
    { code: '+49', flag: '🇩🇪', name: 'ألمانيا' },
  ];

  const nationalities = countries.map(c => c.name);

  const dummyCustomers = [
    { _id: '1', full_name: 'محمد أحمد العمري', phone: '966551234567', national_id: '1234567890', gender: 'Male', nationality: 'السعودية', date_of_birth: '1990-05-15', created_at: new Date() },
  ];

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = { page: currentPage, limit, ...(searchQuery && { search: searchQuery }) };
      const response = await customersAPI.getAll(params);
      
      let customersData = [];
      let paginationData = { totalPages: 1, totalCount: 0 };
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          customersData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          customersData = response.data.data;
          paginationData = response.data.pagination || paginationData;
        }
      }
      
      setCustomers(customersData);
      setTotalPages(paginationData.totalPages || 1);
      setTotalCount(paginationData.totalCount || customersData.length);
    } catch (err) {
      setError('حدث خطأ أثناء تحميل البيانات');
      setCustomers(dummyCustomers);
      setTotalCount(dummyCustomers.length);
    }
    
    setLoading(false);
  }, [currentPage, searchQuery]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleSearch = (e) => { setSearchQuery(e.target.value); setCurrentPage(1); };

  const handleDelete = async () => {
    if (!selectedCustomer) return;
    setDeleting(true);
    try {
      await customersAPI.delete(selectedCustomer._id);
      setCustomers(prev => prev.filter(c => c._id !== selectedCustomer._id));
      setTotalCount(prev => prev - 1);
      setShowDeleteModal(false);
      setSelectedCustomer(null);
    } catch (err) {
      setError('حدث خطأ أثناء حذف العميل');
    }
    setDeleting(false);
  };

  const handleView = (customer) => { setSelectedCustomer(customer); setShowViewModal(true); };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    // Extract country code from phone
    let phoneNumber = customer.phone || '';
    let countryCode = '+966';
    for (const country of countries) {
      if (phoneNumber.startsWith(country.code.replace('+', ''))) {
        countryCode = country.code;
        phoneNumber = phoneNumber.substring(country.code.replace('+', '').length);
        break;
      }
    }
    setEditCountryCode(countryCode);
    setEditData({
      full_name: customer.full_name || '',
      nationality: customer.nationality || 'السعودية',
      identity_type: customer.identity_type || 'ID',
      national_id: customer.national_id || '',
      phone: phoneNumber,
      date_of_birth: customer.date_of_birth ? customer.date_of_birth.split('T')[0] : '',
      email: customer.email || '',
      address: customer.address || '',
      patient_history: customer.patient_history || '',
      reference_mrn: customer.reference_mrn || '',
      gender: customer.gender || 'Male',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCustomer) return;
    setSaving(true);
    try {
      const dataToSend = {
        ...editData,
        phone: editCountryCode.replace('+', '') + editData.phone,
      };
      const response = await customersAPI.update(selectedCustomer._id, dataToSend);
      const updatedCustomer = response.data.data || response.data;
      setCustomers(prev => prev.map(c => c._id === selectedCustomer._id ? { ...c, ...updatedCustomer } : c));
      setShowEditModal(false);
      setSelectedCustomer(null);
    } catch (err) {
      setError('حدث خطأ أثناء تحديث بيانات العميل');
    }
    setSaving(false);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    if (phone.startsWith('+')) return phone;
    if (phone.length > 9) return '+' + phone;
    return phone;
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-';
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const inputClasses = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <Users className="w-6 h-6 text-slate-100" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">قائمة العملاء</h1>
            <p className="text-slate-500 text-sm mt-1">
              إجمالي <span className="font-semibold text-slate-700">{totalCount}</span> عميل
            </p>
          </div>
        </div>
        <motion.button 
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }} 
          onClick={() => navigate('/customers/new')}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span>إضافة عميل</span>
        </motion.button>
      </motion.div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input type="text" value={searchQuery} onChange={handleSearch} placeholder="البحث بالاسم أو رقم الجوال أو رقم الهوية..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white" />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <button onClick={fetchCustomers} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /><span>تحديث</span>
          </button>
        </div>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 text-amber-700">
          <AlertCircle className="w-5 h-5" /><span>{error}</span>
        </motion.div>
      )}

      {/* Table */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
            <p className="text-sm text-slate-500">جاري تحميل العملاء...</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">لا يوجد عملاء</h3>
            <p className="text-sm text-slate-500 mb-4">ابدأ بإضافة عميل جديد</p>
            <button onClick={() => navigate('/customers/new')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all duration-200">
              <Plus className="w-4 h-4" />
              إضافة عميل جديد
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">العميل</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الجوال</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الهوية</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">العمر</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الجنسية</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">تاريخ التسجيل</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <motion.tr 
                    key={customer._id} 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-600">
                          {customer.full_name?.charAt(0) || 'ع'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{customer.full_name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{customer.gender === 'Male' ? 'ذكر' : 'أنثى'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700" dir="ltr">{formatPhone(customer.phone)}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="font-mono text-sm text-slate-700" dir="ltr">{customer.national_id || '-'}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{customer.age || calculateAge(customer.date_of_birth)} سنة</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{customer.nationality || '-'}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{formatDate(customer.created_at)}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button 
                          onClick={() => navigate('/bookings/new', { state: { customer } })} 
                          className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-150"
                          title="حجز موعد"
                        >
                          <Calendar className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button 
                          onClick={() => handleView(customer)} 
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="عرض التفاصيل"
                        >
                          <Eye className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button 
                          onClick={() => handleEdit(customer)} 
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button 
                          onClick={() => { setSelectedCustomer(customer); setShowDeleteModal(true); }} 
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">صفحة {currentPage} من {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"><ChevronRight className="w-5 h-5" /></button>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"><ChevronLeft className="w-5 h-5" /></button>
            </div>
          </div>
        )}
      </motion.div>

      {/* View Modal */}
      <AnimatePresence>
        {showViewModal && selectedCustomer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowViewModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-100">بيانات العميل</h3>
                <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold ${selectedCustomer.gender === 'Female' ? 'bg-pink-500' : 'bg-blue-500'}`}>
                    {selectedCustomer.full_name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-800">{selectedCustomer.full_name}</h4>
                    <p className="text-gray-500">{selectedCustomer.gender === 'Male' ? 'ذكر' : 'أنثى'} • {selectedCustomer.nationality}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-400">رقم الجوال</p><p className="text-gray-800" dir="ltr">{formatPhone(selectedCustomer.phone)}</p></div>
                  <div><p className="text-gray-400">رقم الهوية</p><p className="text-gray-800" dir="ltr">{selectedCustomer.national_id}</p></div>
                  <div><p className="text-gray-400">تاريخ الميلاد</p><p className="text-gray-800">{formatDate(selectedCustomer.date_of_birth)}</p></div>
                  <div><p className="text-gray-400">العمر</p><p className="text-gray-800">{selectedCustomer.age || calculateAge(selectedCustomer.date_of_birth)} سنة</p></div>
                  {selectedCustomer.email && <div className="col-span-2"><p className="text-gray-400">البريد الإلكتروني</p><p className="text-gray-800" dir="ltr">{selectedCustomer.email}</p></div>}
                  {selectedCustomer.address && <div className="col-span-2"><p className="text-gray-400">العنوان</p><p className="text-gray-800">{selectedCustomer.address}</p></div>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedCustomer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowEditModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8" onClick={e => e.stopPropagation()}>
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2"><Edit className="w-5 h-5" /> تعديل بيانات العميل</h3>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              
              <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الاسم الكامل <span className="text-red-500">*</span></label>
                    <input type="text" value={editData.full_name} onChange={e => setEditData({...editData, full_name: e.target.value})} className={inputClasses} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الجنسية</label>
                    <select value={editData.nationality} onChange={e => setEditData({...editData, nationality: e.target.value})} className={inputClasses}>
                      {nationalities.map(nat => <option key={nat} value={nat}>{nat}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">نوع الهوية</label>
                    <select value={editData.identity_type} onChange={e => setEditData({...editData, identity_type: e.target.value})} className={inputClasses}>
                      <option value="ID">هوية وطنية</option>
                      <option value="Passport">جواز سفر</option>
                      <option value="Iqama">إقامة</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">رقم الهوية / جواز السفر <span className="text-red-500">*</span></label>
                    <input type="text" value={editData.national_id} onChange={e => setEditData({...editData, national_id: e.target.value})} className={inputClasses} dir="ltr" />
                  </div>
                </div>

                {/* Row 3 - Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">رقم الجوال <span className="text-red-500">*</span></label>
                  <div className="flex gap-2 max-w-md">
                    <select value={editCountryCode} onChange={e => setEditCountryCode(e.target.value)} className="w-28 px-2 py-2.5 bg-white border border-gray-200 rounded-lg text-sm" dir="ltr">
                      {countries.map(country => <option key={country.code} value={country.code}>{country.flag} {country.code}</option>)}
                    </select>
                    <input type="tel" value={editData.phone} onChange={e => setEditData({...editData, phone: e.target.value})} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg" dir="ltr" placeholder="5XXXXXXXX" />
                  </div>
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ الميلاد <span className="text-red-500">*</span></label>
                    <input type="date" value={editData.date_of_birth} onChange={e => setEditData({...editData, date_of_birth: e.target.value})} className={inputClasses} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">العمر (بالسنوات)</label>
                    <input type="text" value={editData.date_of_birth ? `${calculateAge(editData.date_of_birth)} سنة` : 'يحسب تلقائياً'} className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-gray-600" disabled />
                  </div>
                </div>

                {/* Row 5 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">البريد الإلكتروني (اختياري)</label>
                  <input type="email" value={editData.email} onChange={e => setEditData({...editData, email: e.target.value})} className={`${inputClasses} max-w-md`} dir="ltr" />
                </div>

                {/* Row 6 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">العنوان (اختياري)</label>
                  <input type="text" value={editData.address} onChange={e => setEditData({...editData, address: e.target.value})} className={inputClasses} />
                </div>

                {/* Row 7 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">التاريخ المرضي (اختياري)</label>
                    <textarea value={editData.patient_history} onChange={e => setEditData({...editData, patient_history: e.target.value})} className={inputClasses} rows={2} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الرقم الطبي المرجعي (اختياري)</label>
                    <input type="text" value={editData.reference_mrn} onChange={e => setEditData({...editData, reference_mrn: e.target.value})} className={inputClasses} dir="ltr" />
                  </div>
                </div>

                {/* Gender */}
                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">الجنس</label>
                  <div className="flex justify-center gap-4">
                    <button type="button" onClick={() => setEditData({...editData, gender: 'Male'})}
                      className={`px-10 py-2.5 rounded-full border-2 font-medium transition-all ${editData.gender === 'Male' ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-200 text-gray-600'}`}>
                      ذكر
                    </button>
                    <button type="button" onClick={() => setEditData({...editData, gender: 'Female'})}
                      className={`px-10 py-2.5 rounded-full border-2 font-medium transition-all ${editData.gender === 'Female' ? 'border-pink-500 bg-pink-500 text-white' : 'border-gray-200 text-gray-600'}`}>
                      أنثى
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-slate-50/50 flex justify-end gap-3">
                <button onClick={() => setShowEditModal(false)} className="px-6 py-2.5 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors">إلغاء</button>
                <button onClick={handleSaveEdit} disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
                  {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</> : 'حفظ التغييرات'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-red-600" /></div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف العميل</h3>
                <p className="text-gray-500 mb-6">هل أنت متأكد من حذف العميل "{selectedCustomer?.full_name}"؟<br /><span className="text-sm text-red-500">هذا الإجراء لا يمكن التراجع عنه</span></p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default CustomersList;
